import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, CreditCard, Landmark, Loader2, CalendarCheck, Tag, Plus, Minus, AlertTriangle, Sparkles, Clock, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useContent } from "../context/ContentContext";
import { useItinerary } from "../context/ItineraryContext";
import { useExtras } from "../context/ExtrasContext";
import { unitCount, basisLabel } from "../lib/pricing";
import PageHero from "../components/PageHero";
import { DepositNotice } from "../components/DepositNotice";
import { visibleRoomImages } from "../lib/roomImages";

export default function Booking() {
  const [params] = useSearchParams();
  const { content } = useContent();
  const [rooms, setRooms] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [bookableMedia, setBookableMedia] = useState([]);
  const [roomId, setRoomId] = useState(params.get("room_id") || (typeof localStorage !== "undefined" && localStorage.getItem("ra_room")) || "");
  const [ci, setCi] = useState(params.get("check_in") || (typeof localStorage !== "undefined" ? localStorage.getItem("ra_ci") : "") || "");
  const [co, setCo] = useState(params.get("check_out") || (typeof localStorage !== "undefined" ? localStorage.getItem("ra_co") : "") || "");
  const [guests, setGuests] = useState(Number(params.get("guests")) || Number(typeof localStorage !== "undefined" && localStorage.getItem("ra_guests")) || 2);
  const [form, setForm] = useState({ guest_name: "", guest_email: "", guest_phone: "", notes: "" });
  const [promo, setPromo] = useState(params.get("promo") || "");
  const promoFromUrl = !!params.get("promo");
  const [promoInfo, setPromoInfo] = useState(null);
  useEffect(() => {
    const code = promo.trim();
    if (!code) { setPromoInfo(null); return; }
    let active = true;
    const t = setTimeout(() => {
      api.post("/promotions/validate", { code }).then((r) => { if (active) setPromoInfo(r.data); }).catch(() => { if (active) setPromoInfo(null); });
    }, 400);
    return () => { active = false; clearTimeout(t); };
  }, [promo]);
  const [addons, setAddons] = useState({}); // id -> qty
  const [avail, setAvail] = useState(null);
  const [checking, setChecking] = useState(false);
  const [booking, setBooking] = useState(null);
  const [creating, setCreating] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [arrivalAgree, setArrivalAgree] = useState(false);
  const [arrivalDone, setArrivalDone] = useState(false);
  const [arrivalLoading, setArrivalLoading] = useState(false);
  const [idConsent, setIdConsent] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const itinerary = useItinerary();
  const extras = useExtras();
  const [flashIds, setFlashIds] = useState([]);
  const flash = (id) => {
    setFlashIds((f) => (f.includes(id) ? f : [...f, id]));
    setTimeout(() => setFlashIds((f) => f.filter((x) => x !== id)), 1800);
  };

  useEffect(() => {
    api.get("/rooms").then((r) => { setRooms(r.data); if (!roomId && r.data[0]) setRoomId(r.data[0].id); }).catch(() => {});
    api.get("/experiences").then((r) => setExperiences(r.data)).catch(() => {});
    api.get("/bookable-media").then((r) => setBookableMedia(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!experiences.length) return;
    const urlId = params.get("experience");
    const toAdd = [];
    if (urlId && experiences.some((e) => e.id === urlId)) toAdd.push(urlId);
    itinerary.ids.forEach((id) => { if (experiences.some((e) => e.id === id) && !toAdd.includes(id)) toAdd.push(id); });
    if (!toAdd.length) return;
    setAddons((p) => { const next = { ...p }; toAdd.forEach((id) => { if (!next[id]) next[id] = 1; }); return next; });
    toAdd.forEach((id) => flash(id));
    if (urlId && experiences.some((e) => e.id === urlId) && !itinerary.ids.includes(urlId)) itinerary.add(urlId);
  }, [experiences]); // eslint-disable-line

  useEffect(() => {
    if (!bookableMedia.length) return;
    const entries = Object.entries(extras.items).filter(([id]) => bookableMedia.some((m) => m.id === id));
    if (!entries.length) return;
    setAddons((p) => { const next = { ...p }; entries.forEach(([id, q]) => { next[id] = q; }); return next; });
    entries.forEach(([id]) => flash(id));
  }, [bookableMedia]); // eslint-disable-line

  useEffect(() => { try { if (roomId) localStorage.setItem("ra_room", roomId); } catch { /* ignore */ } }, [roomId]);
  useEffect(() => { try { localStorage.setItem("ra_guests", String(guests)); } catch { /* ignore */ } }, [guests]);

  const room = rooms.find((r) => r.id === roomId);
  const canBook = avail?.available && avail?.min_stay_met;
  const items = [...experiences, ...bookableMedia];
  const nights = ci && co ? Math.max(1, Math.round((new Date(co) - new Date(ci)) / 86400000)) : 1;
  const addonTotal = Object.entries(addons).reduce((sum, [id, qty]) => {
    const e = items.find((x) => x.id === id); return sum + (e ? e.price * unitCount(e.price_basis, qty, guests, nights, e.max_guests) : 0);
  }, 0);
  const roomTotal = avail?.total ?? null;
  const estTotal = roomTotal != null ? roomTotal + addonTotal : null;
  const estDiscount = promoInfo && roomTotal != null ? Math.round(roomTotal * promoInfo.percent) / 100 : 0;
  const estTotalNet = estTotal != null ? Math.round((estTotal - estDiscount) * 100) / 100 : null;

  const setQty = (id, delta) => {
    setAddons((prev) => {
      const next = { ...prev };
      const q = (next[id] || 0) + delta;
      if (q <= 0) delete next[id]; else next[id] = q;
      return next;
    });
    if (delta > 0) flash(id);
  };

  const doCheck = async () => {
    if (!roomId || !ci || !co) { toast.error("Select a room and dates first."); return; }
    setChecking(true); setAvail(null);
    try {
      const { data } = await api.post("/availability/check", { room_id: roomId, check_in: ci, check_out: co });
      setAvail(data);
      if (!data.available) toast.error("Sorry, those dates are unavailable for this suite.");
      else if (!data.min_stay_met) toast.warning(`Minimum stay of ${data.min_stay} nights applies for these dates.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setChecking(false); }
  };

  const createBooking = async (e) => {
    e.preventDefault();
    if (!canBook) { toast.error("Please check availability first."); return; }
    if (!idConsent) { toast.error("Please acknowledge the identity verification requirement to continue."); return; }
    setCreating(true);
    try {
      const addonList = Object.entries(addons).map(([id, quantity]) => ({ id, quantity }));
      const { data } = await api.post("/bookings", { room_id: roomId, check_in: ci, check_out: co, guests: Number(guests), promo_code: promo, addons: addonList, id_consent: idConsent, ...form });
      setBooking(data);
      itinerary.clear();
      extras.clear();
      toast.success("Reservation created — choose how to pay.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const payOnline = async (mode = "full") => {
    setPayLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin, mode });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPayLoading(false);
    }
  };

  const payWithPayPal = async (mode = "full") => {
    setPayLoading(true);
    try {
      const { data } = await api.post("/payments/paypal/create-order", { booking_id: booking.id, origin_url: window.location.origin, mode });
      if (data.approve_url) window.location.href = data.approve_url;
      else { toast.error("Could not start PayPal payment"); setPayLoading(false); }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPayLoading(false);
    }
  };

  const payArrival = async () => {
    if (!arrivalAgree) { toast.error("Please tick the box to agree to the payment terms."); return; }
    setArrivalLoading(true);
    try {
      await api.post(`/bookings/${booking.id}/pay-on-arrival`, { agreed: true });
      setArrivalDone(true);
      toast.success("Booking confirmed — see you soon!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setArrivalLoading(false); }
  };

  return (
    <div>
      <PageHero eyebrow={content?.page_heroes?.booking?.eyebrow || "Availability & Booking"} title={content?.page_heroes?.booking?.title || "Reserve your escape"}
        subtitle={content?.page_heroes?.booking?.subtitle || "Real-time availability, instant confirmation and flexible payment."}
        image={content?.page_heroes?.booking?.image || "https://images.unsplash.com/photo-1758448755969-8791367cf5c5?q=85&w=1600"} />

      <section className="py-16 container-lux grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Step 1 dates */}
          <div className="card-lux p-7">
            <h2 className="font-serif text-2xl text-forest mb-5 flex items-center gap-2"><CalendarCheck className="text-rose" size={22} /> Choose dates & suite</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wide text-stone-500">Suite</label>
                <select data-testid="booking-room-select" value={roomId} onChange={(e) => { setRoomId(e.target.value); setAvail(null); }}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30">
                  {rooms.map((r) => <option key={r.id} value={r.id}>{r.name} — ${r.price}/night (sleeps {r.capacity})</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-stone-500">Check in</label>
                <input data-testid="booking-checkin" type="date" min={today} value={ci} onChange={(e) => { setCi(e.target.value); setAvail(null); }}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-stone-500">Check out</label>
                <input data-testid="booking-checkout" type="date" min={ci || today} value={co} onChange={(e) => { setCo(e.target.value); setAvail(null); }}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-stone-500">Guests</label>
                <input data-testid="booking-guests" type="number" min={1} max={12} value={guests} onChange={(e) => setGuests(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
              </div>
              <div className="flex items-end">
                <button type="button" data-testid="booking-check-availability" onClick={doCheck} disabled={checking} className="btn-dark w-full disabled:opacity-60">
                  {checking ? <Loader2 className="animate-spin" size={16} /> : "Check availability"}
                </button>
              </div>
            </div>
            {avail && (
              <div data-testid="availability-result" className={`mt-4 rounded-lg px-4 py-3 text-sm ${canBook ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                {!avail.available ? "Not available for the selected dates." :
                  !avail.min_stay_met ? (<span className="flex items-center gap-1.5"><AlertTriangle size={15} /> Minimum stay of {avail.min_stay} nights required for these dates.</span>) :
                  `Available! ${avail.nights} night(s) · $${avail.total} for the suite`}
              </div>
            )}
          </div>

          <DepositNotice />

          {/* Add-ons / Packages */}
          {experiences.length > 0 && !booking && (
            <div className="card-lux p-7" data-testid="booking-addons">
              <h2 className="font-serif text-2xl text-forest mb-1 flex items-center gap-2"><Sparkles className="text-gold" size={20} /> Enhance your stay</h2>
              <p className="text-sm text-stone-500 mb-5">Add curated experiences and packages to your reservation.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {experiences.map((e) => {
                  const qty = addons[e.id] || 0;
                  return (
                    <div key={e.id} data-testid={`addon-${e.id}`} className={`rounded-xl border p-4 flex gap-3 transition-all duration-500 ${flashIds.includes(e.id) ? "ring-2 ring-gold shadow-lg bg-gold/10" : qty ? "border-rose bg-rose/5" : "border-stone-200"}`}>
                      <img src={e.image} alt={e.title} className="h-16 w-16 rounded-lg object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-forest text-sm leading-tight">{e.title}</div>
                        <div className="text-xs text-stone-400 mt-0.5">${e.price} pp · {e.category}</div>
                        {qty === 0 ? (
                          <button type="button" onClick={() => setQty(e.id, 1)} data-testid={`addon-add-${e.id}`} className="mt-2 text-xs font-semibold text-rose hover:text-rose-dark flex items-center gap-1"><Plus size={13} /> Add</button>
                        ) : (
                          <div className="mt-2 inline-flex items-center gap-2">
                            <button type="button" onClick={() => setQty(e.id, -1)} className="h-6 w-6 rounded-full bg-white border border-stone-200 flex items-center justify-center"><Minus size={12} /></button>
                            <span className="text-sm font-semibold w-5 text-center" data-testid={`addon-qty-${e.id}`}>{qty}</span>
                            <button type="button" onClick={() => setQty(e.id, 1)} className="h-6 w-6 rounded-full bg-rose text-white flex items-center justify-center"><Plus size={12} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(() => { const cart = bookableMedia.filter((m) => addons[m.id]); return cart.length > 0 && !booking && (
            <div className="card-lux p-7" data-testid="booking-extras">
              <h2 className="font-serif text-2xl text-forest mb-1 flex items-center gap-2"><Sparkles className="text-gold" size={20} /> From the gallery</h2>
              <p className="text-sm text-stone-500 mb-5">Experiences you added while browsing the gallery.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {cart.map((m) => { const qty = addons[m.id] || 0; return (
                  <div key={m.id} data-testid={`extra-${m.id}`} className={`rounded-xl border p-4 flex gap-3 transition-all duration-500 ${flashIds.includes(m.id) ? "ring-2 ring-gold shadow-lg bg-gold/10" : "border-rose bg-rose/5"}`}>
                    <img src={m.url} alt={m.title} className="h-16 w-16 rounded-lg object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-forest text-sm leading-tight">{m.title || "Experience"}</div>
                      <div className="text-xs text-stone-400 mt-0.5">${m.price} {basisLabel(m.price_basis)}{m.subcategory ? ` · ${m.subcategory}` : m.album ? ` · ${m.album}` : ""}</div>
                      <div className="mt-2 inline-flex items-center gap-2">
                        <button type="button" onClick={() => setQty(m.id, -1)} className="h-6 w-6 rounded-full bg-white border border-stone-200 flex items-center justify-center"><Minus size={12} /></button>
                        <span className="text-sm font-semibold w-5 text-center" data-testid={`extra-qty-${m.id}`}>{qty}</span>
                        <button type="button" onClick={() => setQty(m.id, 1)} className="h-6 w-6 rounded-full bg-rose text-white flex items-center justify-center"><Plus size={12} /></button>
                      </div>
                    </div>
                  </div>
                ); })}
              </div>
            </div>
          ); })()}

          {/* Step 2 guest details */}
          {!booking && (
            <form onSubmit={createBooking} className="card-lux p-7" data-testid="booking-guest-form">
              <h2 className="font-serif text-2xl text-forest mb-5">Your details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Full name" data-testid="booking-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                  className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
                <input required type="email" placeholder="Email" data-testid="booking-email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                  className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
                <input placeholder="Phone" data-testid="booking-phone" value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                  className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
                <div className="relative">
                  <Tag size={15} className="absolute left-3 top-3.5 text-stone-400" />
                  <input placeholder="Promo code (optional)" data-testid="booking-promo" value={promo} onChange={(e) => setPromo(e.target.value)}
                    className="w-full rounded-lg border border-stone-200 pl-9 pr-24 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
                  {promo && promoFromUrl && <span data-testid="promo-applied" className="absolute right-2 top-2.5 inline-flex items-center gap-1 rounded-full bg-green-50 text-green-700 text-[11px] font-semibold px-2 py-1"><Check size={12} /> Applied</span>}
                </div>
                <textarea rows={3} placeholder="Special requests (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="sm:col-span-2 w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
              </div>
              <div className="mt-6 rounded-xl bg-forest/5 border border-forest/15 px-4 py-4" data-testid="id-consent-block">
                <div className="flex items-center gap-2 text-forest font-semibold text-sm"><ShieldCheck size={16} className="text-gold" /> Identity verification before check-in</div>
                <p className="text-xs text-stone-500 mt-1 leading-relaxed">For security and compliance purposes, valid photographic identification will be required before check-in. Details of how to upload your identification securely will be provided after your reservation is confirmed.</p>
                <label className="flex items-start gap-2 text-xs text-stone-700 leading-relaxed mt-3 cursor-pointer">
                  <input type="checkbox" checked={idConsent} onChange={(e) => setIdConsent(e.target.checked)} data-testid="id-consent-checkbox" className="mt-0.5" required />
                  <span>I acknowledge that identity verification is required before check-in and consent to providing identification documents as outlined in the <a href="/contact" className="text-rose underline">Terms and Conditions</a>.</span>
                </label>
              </div>
              <DepositNotice className="mt-5" />
              <button type="submit" disabled={creating || !canBook || !idConsent} data-testid="booking-submit" className="btn-primary w-full mt-5 disabled:opacity-50">
                {creating ? <Loader2 className="animate-spin" size={16} /> : "Continue to payment"}
              </button>
            </form>
          )}

          {/* Step 3 payment */}
          {booking && !arrivalDone && (() => {
            const po = content?.payment_options || {};
            const depPct = po.deposit_percent ?? 30;
            const depAmt = Math.round(booking.total * depPct) / 100;
            const balance = Math.round((booking.total - depAmt) * 100) / 100;
            return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-lux p-7" data-testid="booking-payment">
              <div className="flex items-center gap-2 text-green-700 mb-2"><Check size={18} /> Reservation #{booking.code || booking.id.slice(0, 8).toUpperCase()} created</div>
              {booking.manage_url && <a href={booking.manage_url} data-testid="manage-link" className="text-sm text-rose underline mb-4 inline-block">View or manage this booking →</a>}
              <h2 className="font-serif text-2xl text-forest mb-5">How would you like to pay?</h2>
              <DepositNotice className="mb-5" />
              <div className="rounded-xl bg-forest/5 border border-forest/15 px-4 py-3 text-sm text-forest mb-5" data-testid="pending-verification-note"><ShieldCheck size={15} className="inline mr-1 text-gold" /> A valid photo ID will be required before check-in. After your reservation is confirmed, we'll email you a secure link to upload it.</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <button onClick={() => payOnline("full")} disabled={payLoading} data-testid="pay-online-button" className="rounded-xl border-2 border-rose bg-rose/5 p-5 text-left hover:bg-rose/10 transition-colors disabled:opacity-60">
                  <CreditCard className="text-rose mb-2" />
                  <div className="font-semibold text-forest">Pay in full — ${booking.total}</div>
                  <div className="text-xs text-stone-500 mt-1">Secure card payment. Visa, Mastercard, Apple & Google Pay.</div>
                </button>
                {po.deposit_enabled !== false && (
                  <button onClick={() => payOnline("deposit")} disabled={payLoading} data-testid="pay-deposit-button" className="rounded-xl border border-gold bg-gold/5 p-5 text-left hover:bg-gold/10 transition-colors disabled:opacity-60">
                    <CreditCard className="text-gold mb-2" />
                    <div className="font-semibold text-forest">Pay {depPct}% deposit — ${depAmt}</div>
                    <div className="text-xs text-stone-500 mt-1">Confirm now, pay the ${balance} balance before arrival.</div>
                  </button>
                )}
                <div className="rounded-xl border border-[#0070ba]/40 bg-[#0070ba]/5 p-5 sm:col-span-2" data-testid="pay-paypal-box">
                  <div className="font-semibold text-[#003087] mb-1">Pay with <span className="italic">PayPal</span></div>
                  <div className="text-xs text-stone-500 mb-3">Use your PayPal balance, card or bank — you'll be redirected to PayPal to approve.</div>
                  <div className="flex flex-wrap gap-3">
                    <button onClick={() => payWithPayPal("full")} disabled={payLoading} data-testid="paypal-full-button" className="rounded-lg bg-[#ffc439] text-[#003087] font-bold text-sm px-5 py-2.5 hover:brightness-95 transition disabled:opacity-60">PayPal — pay ${booking.total}</button>
                    {po.deposit_enabled !== false && (
                      <button onClick={() => payWithPayPal("deposit")} disabled={payLoading} data-testid="paypal-deposit-button" className="rounded-lg border border-[#0070ba] text-[#003087] font-semibold text-sm px-5 py-2.5 hover:bg-[#0070ba]/10 transition disabled:opacity-60">PayPal — {depPct}% deposit ${depAmt}</button>
                    )}
                  </div>
                </div>
                <div className="rounded-xl border border-stone-200 p-5" data-testid="pay-offline-box">
                  <Landmark className="text-forest mb-2" />
                  <div className="font-semibold text-forest">Pay offline</div>
                  <div className="text-xs text-stone-500 mt-1 leading-relaxed">Bank transfer or EcoCash. Held as pending until our team confirms.<br />Reference: <span className="font-mono">{booking.code || booking.id.slice(0, 8).toUpperCase()}</span></div>
                </div>
                {po.pay_on_arrival_enabled !== false && (
                  <div className="rounded-xl border border-stone-200 p-5 sm:col-span-2" data-testid="pay-arrival-box">
                    <div className="flex items-center gap-2 mb-2"><Clock className="text-forest" /><span className="font-semibold text-forest">Pay on arrival</span></div>
                    <label className="flex items-start gap-2 text-xs text-stone-600 leading-relaxed">
                      <input type="checkbox" checked={arrivalAgree} onChange={(e) => setArrivalAgree(e.target.checked)} data-testid="arrival-agree" className="mt-0.5" />
                      <span>{po.arrival_terms || "I agree to pay the full amount before check-in. The Rose Air BnB reserves the right to refuse entry and ask guests to leave if payment is not made on arrival."}</span>
                    </label>
                    <button onClick={payArrival} disabled={arrivalLoading || !arrivalAgree} data-testid="pay-arrival-button" className="btn-dark mt-3 disabled:opacity-50">
                      {arrivalLoading ? <Loader2 className="animate-spin" size={16} /> : "Confirm — pay on arrival"}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
            );
          })()}

          {arrivalDone && booking && (
            <div className="card-lux p-8 text-center" data-testid="arrival-confirmed">
              <ShieldCheck className="mx-auto text-gold" size={48} />
              <h2 className="font-serif text-3xl text-forest mt-3">Reservation Received</h2>
              <p className="text-stone-500 mt-2">Your reservation is confirmed. You'll settle the full amount of <strong>${booking.total}</strong> before check-in. A valid photo ID will be required before arrival — we'll email you a secure link to upload it.</p>
              {booking.manage_url && <a href={booking.manage_url} className="btn-primary mt-5 inline-flex">View / manage booking</a>}
            </div>
          )}
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="card-lux overflow-hidden">
            {room && <img src={visibleRoomImages(room)[0]} alt={room.name} className="h-40 w-full object-cover" />}
            <div className="p-6">
              <h3 className="font-serif text-xl text-forest">{room?.name || "Select a suite"}</h3>
              <div className="mt-4 space-y-2 text-sm text-stone-600">
                <div className="flex justify-between"><span>Check-in</span><span>{ci || "—"}</span></div>
                <div className="flex justify-between"><span>Check-out</span><span>{co || "—"}</span></div>
                <div className="flex justify-between"><span>Nights</span><span>{avail?.nights ?? "—"}</span></div>
                <div className="flex justify-between"><span>Suite subtotal</span><span>${booking?.subtotal ?? roomTotal ?? "—"}</span></div>
                {(booking?.addons?.length ? booking.addons : Object.entries(addons).map(([id, quantity]) => { const e = items.find((x) => x.id === id); return e ? { id, title: e.title, quantity, line_total: e.price * unitCount(e.price_basis, quantity, guests, nights, e.max_guests) } : null; }).filter(Boolean)).map((a) => (
                  <div key={a.id} data-testid={`summary-addon-${a.id}`} className={`flex justify-between text-xs transition-colors duration-500 rounded ${flashIds.includes(a.id) ? "bg-gold/25 text-forest px-1 -mx-1" : "text-stone-500"}`}><span>+ {a.title} ×{a.quantity}</span><span>${a.line_total}</span></div>
                ))}
                {booking?.discount > 0 && <div className="flex justify-between text-xs text-green-700"><span>Promo {booking.promo}</span><span>−${booking.discount}</span></div>}
                {!booking && estDiscount > 0 && <div data-testid="promo-preview" className="flex justify-between text-xs text-green-700 font-medium"><span>Promo {promoInfo.code} (−{promoInfo.percent}%)</span><span>−${estDiscount}</span></div>}
              </div>
              <div className="border-t border-stone-200 mt-4 pt-4 flex justify-between items-center">
                <span className="text-stone-500 text-sm">{booking ? "Total" : "Estimated total"}</span>
                <span className="font-serif text-2xl text-rose" data-testid="booking-total">${booking?.total ?? estTotalNet ?? "—"}</span>
              </div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
